import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { gql, useMutation, useQuery, useLazyQuery } from "@apollo/client";
import "./IncentivesTab.css";

// Phase F.2 cross-user view. Lists every row from the socialIncentives
// DDB table — paid subscribers and non-paying users alike — so the
// super-admin can audit perks without drilling per-Stripe-customer in
// the Subscriptions tab. "Add incentive" opens a search modal backed
// by searchSocialUsers so an admin can attribute a perk to ANY user,
// not just paying subscribers (the Stripe-only path still exists in
// SubscriptionsTab for crediting account balance).

const ALL_INCENTIVES = gql`
  query AllSocialIncentives($search: String, $limit: Int) {
    allSocialIncentives(search: $search, limit: $limit) {
      id
      createdAt
      type
      description
      amount
      currency
      captainEmail
      customerId
      createdBy
      notes
      eventId
    }
  }
`;

const SEARCH_USERS = gql`
  query SearchSocialUsersForIncentive($query: String!) {
    searchSocialUsers(query: $query) {
      userId
      email
      firstName
      lastName
      name
      phone
      paid
      approved
      stripeEmail
    }
  }
`;

const ADD_INCENTIVE = gql`
  mutation AddSocialIncentiveCrossUser($input: SocialIncentiveInput!) {
    addSocialIncentive(input: $input) {
      id
      createdAt
      type
      description
      amount
      currency
      captainEmail
      customerId
      createdBy
    }
  }
`;

const DELETE_INCENTIVE = gql`
  mutation DeleteSocialIncentiveCrossUser($id: ID!) {
    deleteSocialIncentive(id: $id)
  }
`;

const SEARCH_DEBOUNCE_MS = 300;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatAmount = (cents, currency) => {
  if (typeof cents !== "number") return "";
  const dollars = cents / 100;
  const cur = (currency || "usd").toUpperCase();
  return `${cur === "USD" ? "$" : ""}${dollars.toFixed(2)}${
    cur === "USD" ? "" : ` ${cur}`
  }`;
};

const IncentivesTab = () => {
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const debounceRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Debounce the table search so we don't fire a scan per keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const { data, loading, error, refetch } = useQuery(ALL_INCENTIVES, {
    variables: { search: appliedSearch || null, limit: 200 },
    fetchPolicy: "network-only",
  });
  const [deleteIncentive] = useMutation(DELETE_INCENTIVE);

  const rows = data?.allSocialIncentives || [];

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete this perk?\n\n${row.description || row.type || row.id}`
      )
    ) {
      return;
    }
    try {
      await deleteIncentive({ variables: { id: row.id } });
      refetch();
    } catch (err) {
      window.alert(`Delete failed: ${err.message || err}`);
    }
  };

  const handleAdded = useCallback(() => {
    setShowAddModal(false);
    refetch();
  }, [refetch]);

  return (
    <div className="incentives-tab">
      <div className="incentives-tab__header">
        <h2 className="incentives-tab__title">Incentives</h2>
        <button
          type="button"
          className="incentives-tab__btn incentives-tab__btn--primary"
          onClick={() => setShowAddModal(true)}
        >
          + Add incentive
        </button>
      </div>
      <p className="incentives-tab__hint">
        Every non-monetary perk ever logged, across paying and non-paying
        users. Stripe account-balance credits are still managed per-customer
        in the Subscriptions tab — this view is the local ledger only.
      </p>

      <div className="incentives-tab__toolbar">
        <input
          type="search"
          className="incentives-tab__search"
          placeholder="Filter by email, type, description, customerId…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <span className="incentives-tab__count">
          {loading ? "Loading…" : `${rows.length} entr${rows.length === 1 ? "y" : "ies"}`}
        </span>
      </div>

      {error && (
        <div className="incentives-tab__error">
          Error loading incentives: {error.message}
        </div>
      )}

      <div className="incentives-tab__table-wrap">
        <table className="incentives-tab__table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Who</th>
              <th>Type</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Added by</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="incentives-tab__empty">
                  {appliedSearch
                    ? `No incentives match "${appliedSearch}".`
                    : "No incentives logged yet."}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="incentives-tab__date">{formatDate(r.createdAt)}</td>
                <td>
                  <div className="incentives-tab__who">
                    {r.captainEmail || (
                      <span className="incentives-tab__muted">
                        {r.customerId ? `cust:${r.customerId}` : "(unattributed)"}
                      </span>
                    )}
                    {r.captainEmail && r.customerId && (
                      <div className="incentives-tab__muted-sub">
                        cust:{r.customerId}
                      </div>
                    )}
                  </div>
                </td>
                <td>{r.type || ""}</td>
                <td className="incentives-tab__description">
                  {r.description || ""}
                  {r.notes && (
                    <div className="incentives-tab__muted-sub">{r.notes}</div>
                  )}
                  {r.eventId && (
                    <div className="incentives-tab__muted-sub">
                      event: {r.eventId}
                    </div>
                  )}
                </td>
                <td>{formatAmount(r.amount, r.currency)}</td>
                <td className="incentives-tab__muted">{r.createdBy || ""}</td>
                <td>
                  <button
                    type="button"
                    className="incentives-tab__btn incentives-tab__btn--danger-icon"
                    onClick={() => handleDelete(r)}
                    title="Delete this perk"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <AddIncentiveModal
          onClose={() => setShowAddModal(false)}
          onAdded={handleAdded}
        />
      )}
    </div>
  );
};

// Add-incentive modal: two-step flow.
//   1. Search for a user (any socialUsers row, paying or not).
//   2. Confirm pick → fill type / description / optional amount + notes.
// addSocialIncentive only requires customerId OR captainEmail; this form
// always sends captainEmail (since picking happens via searchSocialUsers).
// If the picked user has a Stripe link the row carries customerId too,
// so the Subscriptions-tab history view picks it up via either match.
const AddIncentiveModal = ({ onClose, onAdded }) => {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [type, setType] = useState("dinner");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const debounceRef = useRef(null);

  const [runSearch, { data: searchData, loading: searchLoading, error: searchError }] =
    useLazyQuery(SEARCH_USERS, { fetchPolicy: "network-only" });
  const [addIncentive] = useMutation(ADD_INCENTIVE);

  useEffect(() => {
    if (picked) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) return;
    debounceRef.current = setTimeout(() => {
      runSearch({ variables: { query: q } });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, picked, runSearch]);

  const results = searchData?.searchSocialUsers || [];

  const displayName = useMemo(() => {
    if (!picked) return "";
    return (
      [picked.firstName, picked.lastName].filter(Boolean).join(" ") ||
      picked.name ||
      picked.email ||
      picked.userId
    );
  }, [picked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!picked) return;
    const email = (picked.email || "").trim().toLowerCase();
    if (!email) {
      window.alert(
        "This user has no email on record — pick someone with an email so the perk can be attributed."
      );
      return;
    }
    const cleanType = type.trim();
    const cleanDesc = description.trim();
    if (!cleanType) {
      window.alert("Type is required.");
      return;
    }
    if (!cleanDesc) {
      window.alert("Description is required.");
      return;
    }
    let amountCents = null;
    const amountTrim = amount.trim();
    if (amountTrim) {
      const parsed = parseFloat(amountTrim);
      if (!Number.isFinite(parsed) || parsed < 0) {
        window.alert("Amount must be a non-negative number.");
        return;
      }
      amountCents = Math.round(parsed * 100);
    }
    setSubmitting(true);
    try {
      await addIncentive({
        variables: {
          input: {
            captainEmail: email,
            type: cleanType,
            description: cleanDesc,
            amount: amountCents,
            currency: amountCents != null ? "usd" : null,
            notes: notes.trim() || null,
          },
        },
      });
      onAdded();
    } catch (err) {
      window.alert(`Add perk failed: ${err.message || err}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="incentives-modal__backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="incentives-modal__panel" onClick={(e) => e.stopPropagation()}>
        <div className="incentives-modal__header">
          <h3 className="incentives-modal__title">Add incentive</h3>
          <button
            type="button"
            className="incentives-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {!picked ? (
          <>
            <p className="incentives-modal__hint">
              Search for any user (paying or not). Pick them to continue.
            </p>
            <input
              type="search"
              className="incentives-modal__search"
              placeholder="Search by first or last name, email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            {query.trim().length > 0 && query.trim().length < 2 && (
              <div className="incentives-modal__hint">Type at least 2 characters…</div>
            )}
            {searchLoading && <div className="incentives-modal__hint">Searching…</div>}
            {searchError && (
              <div className="incentives-modal__error">
                Search failed: {searchError.message}
              </div>
            )}
            {!searchLoading && !searchError && query.trim().length >= 2 && results.length === 0 && (
              <div className="incentives-modal__hint">
                No matches for "{query.trim()}".
              </div>
            )}
            <div className="incentives-modal__results">
              {results.map((u) => {
                const name =
                  [u.firstName, u.lastName].filter(Boolean).join(" ") ||
                  u.name ||
                  u.email ||
                  u.userId;
                return (
                  <div key={u.userId} className="incentives-modal__row">
                    <div className="incentives-modal__row-main">
                      <div className="incentives-modal__row-name">{name}</div>
                      <div className="incentives-modal__row-meta">
                        <span>{u.email || "(no email)"}</span>
                        {typeof u.paid === "boolean" && (
                          <span>· {u.paid ? "paid" : "unpaid"}</span>
                        )}
                        {typeof u.approved === "boolean" && (
                          <span>· {u.approved ? "approved" : "not approved"}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="incentives-modal__btn incentives-modal__btn--primary"
                      onClick={() => setPicked(u)}
                      disabled={!u.email}
                      title={u.email ? "Pick this user" : "No email on record"}
                    >
                      Pick
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="incentives-modal__form">
            <div className="incentives-modal__picked">
              <div>
                <div className="incentives-modal__picked-name">{displayName}</div>
                <div className="incentives-modal__row-meta">
                  {picked.email || "(no email)"}
                  {typeof picked.paid === "boolean" && (
                    <span> · {picked.paid ? "paid" : "unpaid"}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="incentives-modal__btn"
                onClick={() => setPicked(null)}
                disabled={submitting}
              >
                Change
              </button>
            </div>

            <label className="incentives-modal__label">
              Type
              <input
                type="text"
                value={type}
                onChange={(e) => setType(e.target.value)}
                placeholder="dinner / trip / gift / free-form"
                disabled={submitting}
                required
              />
            </label>
            <label className="incentives-modal__label">
              Description
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="May volleyball host dinner — Maple St Biscuit"
                disabled={submitting}
                required
              />
            </label>
            <label className="incentives-modal__label">
              Estimated value (USD, optional)
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Leave blank for non-monetary perks"
                disabled={submitting}
              />
            </label>
            <label className="incentives-modal__label">
              Notes (optional)
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                disabled={submitting}
              />
            </label>

            <div className="incentives-modal__footer">
              <button
                type="button"
                className="incentives-modal__btn"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="incentives-modal__btn incentives-modal__btn--primary"
                disabled={submitting}
              >
                {submitting ? "Adding…" : "Add incentive"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default IncentivesTab;
